import React from 'react';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';
import MathquillUIElement from 'mathquill-ui';

import { t } from '../../../../services/i18nService';
import stringUtils from '../../../../qti/utils/StringUtils';
import a11yIIBScreenReaderService, { A11yForIIBScreenReaderIteration } from '../../../../services/a11yIIBScreenReaderService';

import styles from './mathWorking.scss';

const MAX_CONTENT_WIDTH = 700;
// Timeout to wait for MathQuill to render its DOM elements before attaching keyboard accessibility handlers
// This delay accounts for MathQuill's asynchronous rendering process
const MATHQUILL_RENDER_DELAY_MS = 100;

class MathWorkingInput extends React.PureComponent {
    static propTypes = {
        callback: PropTypes.func.isRequired, // callback(responseIdentifier, [simpleChoice identifiers])
        extraChildren: PropTypes.object,
        handleInteractionReady: PropTypes.func.isRequired,
        isReadOnly: PropTypes.bool,
        mathRowModel: PropTypes.object,
        model: PropTypes.object.isRequired, // model taken from responseModels related to this interaction
        onInstanceReady: PropTypes.func,
        userResponse: PropTypes.array.isRequired,
        parent: PropTypes.object.isRequired, // TextEntryInteraction || MathWorkingInteraction instance
        mathSymbolPalettePreset: PropTypes.object,
    };

    static defaultProps = {
        mathRowModel: {},
    };

    constructor(props) {
        super(props);
        this.state = { isTooWide: false };

        // Calculate once to avoid recalculating on every mount/update
        this.isA11yV4Enabled = a11yIIBScreenReaderService
            .isA11yIterationForIIBScreenReaderSupported(A11yForIIBScreenReaderIteration.v4);

        // Store references to all trigger buttons for cleanup
        this.triggerButtons = [];
    }

    componentDidMount() {
        this.initMathquill();
        this.addKeyboardAccessibilityHandlers();
    }

    // eslint-disable-next-line camelcase,react/sort-comp
    UNSAFE_componentWillUpdate(nextProps) {
        if (this.props.isReadOnly !== nextProps.isReadOnly) {
            ReactDOM.unmountComponentAtNode(this.mathQuillTargetElement);
        }
    }

    componentDidUpdate(prevProps) {
        const { isReadOnly = false } = this.props;
        if (isReadOnly) {
            this.cleanupKeyboardAccessibilityHandlers();
            this.initMathquill();
        } else if (!isReadOnly && prevProps.isReadOnly) {
            // changed from readonly to editable
            this.cleanupKeyboardAccessibilityHandlers();
            ReactDOM.unmountComponentAtNode(this.mathQuillTargetElement);
            this.initMathquill();
            // Re-add keyboard accessibility handlers after re-initialization
            this.addKeyboardAccessibilityHandlers();
        } else if (this.props.userResponse.length === 0 && prevProps.userResponse.length > 0) {
            // changed userResponses (reset of question)
            this.cleanupKeyboardAccessibilityHandlers();
            ReactDOM.unmountComponentAtNode(this.mathQuillTargetElement);
            this.initMathquill();
            // Re-add keyboard accessibility handlers after re-initialization
            this.addKeyboardAccessibilityHandlers();
        }
    }

    componentWillUnmount() {
        this.cleanupKeyboardAccessibilityHandlers();

        if (this.mathQuillTargetElement) {
            ReactDOM.unmountComponentAtNode(this.mathQuillTargetElement);
        }
    }

    cleanupKeyboardAccessibilityHandlers = () => {
        // Only cleanup if v4 accessibility was enabled (handlers were only added in this case)
        if (!this.isA11yV4Enabled) {
            return;
        }

        // Clean up keyboard accessibility event listeners to prevent memory leaks
        if (this.triggerButtons && this.triggerButtons.length > 0) {
            this.triggerButtons.forEach((button) => {
                if (button) {
                    button.removeEventListener('keydown', this.handleTriggerKeyDown);
                }
            });
            this.triggerButtons = [];
        }
    };

    initMathquill = () => {
        if (typeof this.props.onInstanceReady === 'function') {
            this.props.onInstanceReady(this);
        }

        if (this.props.mathRowModel) {
            this.props.mathRowModel.instance = this;
        }
        const { isReadOnly = false, mathSymbolPalettePreset } = this.props;
        let mathquillInitText = this.props.parent.getUserResponse();
        mathquillInitText = (this.props.model.variant === 'math' && this.props.mathRowModel)
            ? this.props.mathRowModel.mathText
            : mathquillInitText;
        const isA11yIIBScreenReaderSupportedV3 = a11yIIBScreenReaderService
            .isA11yIterationForIIBScreenReaderSupported(A11yForIIBScreenReaderIteration.v3);

        if (isReadOnly) {
            ReactDOM.render(
                <MathquillUIElement
                    key={`mathquill-${this.props.model.identifier}`}
                    triggerOnClick={false}
                    isStaticMathElement
                    id={this.getMathEditableId()}
                    mathquillText={mathquillInitText}
                    onInstanceReady={this.onMathquillUIElementInstanceReady}
                    isA11yIIBScreenReaderEnabled={isA11yIIBScreenReaderSupportedV3}
                />,
                this.mathQuillTargetElement
            );
            this.setMathInputSize();
        } else {
            ReactDOM.render(
                <MathquillUIElement
                    key={`mathquill-${this.props.model.identifier}`}
                    triggerOnClick={false}
                    disableMenuCache
                    id={this.getMathEditableId()}
                    wrapper={this.mathQuillTargetElement}
                    extraChildren={this.props.extraChildren}
                    mathquillText={mathquillInitText}
                    mathEditableChangeCallback={this.onMathEditableChange}
                    onInstanceReady={this.onMathquillUIElementInstanceReady}
                    menuData={mathSymbolPalettePreset}
                    isA11yIIBScreenReaderEnabled={isA11yIIBScreenReaderSupportedV3}
                />,
                this.mathQuillTargetElement
            );
        }
    };

    handleUserResponse = () => {
        const { model } = this.props;
        if (typeof this.props.parent.handleUserResponse === 'function') {
            this.props.parent.handleUserResponse();
        } else {
            this.props.callback(model.identifier, [this.getValue()]);
        }
    };

    setValue = (userResponse) => {
        if (this.mathquillUIElement) {
            this.mathquillUIElement.setMathText(userResponse);
        }
    };

    getValue = () => {
        return this.mathquillUIElement ? this.mathquillUIElement.getLatex() : '';
    };

    getRenderedContentWidth = () => {
        return this.mathquillUIElement ? this.mathquillUIElement.getRenderedContentWidth() : 0;
    };

    handleTriggerKeyDown = (event) => {
        // Only handle keyboard events when v4 accessibility is enabled
        if (!this.isA11yV4Enabled) {
            return;
        }

        // Make the trigger button keyboard accessible by responding to Enter and Space
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            event.target.click();
        }
    };

    addKeyboardAccessibilityHandlers = () => {
        // Only add handlers if v4 accessibility is enabled and component is not read-only
        if (!this.isA11yV4Enabled || this.props.isReadOnly) {
            return;
        }

        // Wait for MathQuill to render its DOM elements
        setTimeout(() => {
            if (this.mathQuillTargetElement) {
                // Use querySelectorAll to find ALL trigger buttons (for multiple rows)
                const triggerButtons = this.mathQuillTargetElement.querySelectorAll('.mathquill-ui-trigger');
                if (triggerButtons && triggerButtons.length > 0) {
                    // Clear existing references before adding new ones
                    this.triggerButtons = [];

                    triggerButtons.forEach((triggerButton) => {
                        // Make trigger button keyboard focusable
                        triggerButton.setAttribute('tabindex', '0');
                        triggerButton.addEventListener('keydown', this.handleTriggerKeyDown);
                        // Store reference for cleanup
                        this.triggerButtons.push(triggerButton);
                    });
                }
            }
        }, MATHQUILL_RENDER_DELAY_MS);
    };

    getMathEditableId = () => {
        if (this.mathEditableId) {
            // use existing (cached) value of this.mathEditableId
        } else if (this.props.mathRowModel.id) {
            this.mathEditableId = this.props.mathRowModel.id;
        } else if (this.props.model.variant === 'math') {
            this.mathEditableId = stringUtils.getUID();
        } else {
            this.mathEditableId = `${this.props.model.target}_valueId`;
        }

        return this.mathEditableId;
    };

    onMathEditableChange = () => {
        if (this.props.mathRowModel) {
            this.props.mathRowModel.mathText = this.getValue();
        }
        const width = this.getRenderedContentWidth();
        this.setState({ isTooWide: width > MAX_CONTENT_WIDTH });
        this.handleUserResponse();
    };

    onMathquillUIElementInstanceReady = (instance) => {
        this.mathquillUIElement = instance;
        if (this.props.mathRowModel && this.props.mathRowModel.autoFocus) {
            instance.onMathEditorClicked();
            this.props.mathRowModel.autoFocus = false;
        }
        this.setMathInputSize();
        this.props.handleInteractionReady(true);
    };

    setMathInputSize = () => {
        const { isReadOnly = false } = this.props;
        const inputSize = `${MAX_CONTENT_WIDTH}px`;
        if (isReadOnly) {
            this.mathQuillTargetElement.style.minWidth = inputSize;
            return;
        }

        const editableElement = this.mathQuillTargetElement.querySelector('.mathquill-editable');
        if (editableElement) {
            editableElement.style.width = inputSize;
        }
    };

    render() {
        const { isReadOnly = false } = this.props;
        const { isTooWide } = this.state;

        if (isReadOnly) {
            return <span className="mathquill-ui-static" ref={(div) => { this.mathQuillTargetElement = div; }} />;
        }

        const rootClassNames = [
            'interaction',
            'mathquill-outer-wrapper',
            isTooWide ? styles.isTooWide : '',
            this.isA11yV4Enabled ? 'a11y-v4-enabled' : '',
        ].filter(Boolean).join(' ');
        return (
            <span className={rootClassNames}>
                {
                    isTooWide ?
                        <span className={styles.message}>
                            {t('interactions.mathWorking.tooLongWarning')}
                        </span> : null
                }
                <span className="mathquill-ui-wrapper" ref={(div) => { this.mathQuillTargetElement = div; }} />
            </span>
        );
    }
}

export default MathWorkingInput;
